import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../api/useApi";
import { groups } from "../../api/groupQueries";

import { updateCreateWorkGroupFlag } from "../../api/workgroupMutations";

import DataTable from "../../components/Table/DataTable";
import textFilterToWhereClause from "../../components/Table/helpers/textFilterToWhere";
import Button from "../../components/Button";
import { MdArchive } from "react-icons/md";
import Modal from "../../components/Modal";
import Spinner from "../../components/Loaders/Spinner";
import ErrorMessages from "../../components/Notifications/ErrorMessages";
import TableButton from "../../components/Button/TableButton";
import AddGroups from "./AddGroups";

const Organization = () => {
  const [{ loading, errors, data }, doFetch] = useApi();
  const [
    { loading: removalLoading, errors: removalErrors, data: removalData },
    remove,
  ] = useApi();
  const [{ errors: syncErrors, data: syncData }] = useApi();

  const [showConfirm, setShowConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  // Bumped to force <DataTable> to refetch the current page after a mutation.
  const [refreshKey, setRefreshKey] = useState(0);

  const workgroups = data?.groups?.edges ?? [];
  const totalCount = data?.groups?.totalCount;
  const pageInfo = data?.groups?.pageInfo;

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.displayName,
    },
    {
      Header: "Enabled",
      id: "enabled",
      // Controlled by the Show/Hide Disabled toggle (base where) — not a
      // free-text filter target.
      disableColumnMenu: true,
      accessor: (d) => d?.node?.createWorkGroup,
      Cell: ({ row: { original } }) => {
        return original?.node?.createWorkGroup?.toString();
      },
    },
    {
      Header: "",
      id: "actions",
      maxWidth: 150,
      disableResizing: true,
      Cell: ({ row: { original } }) => {
        const enabled = original?.node?.createWorkGroup;

        return (
          <>
            <TableButton
              title="Toggle Enabled"
              type="button"
              danger={enabled}
              onClick={() =>
                setOrgToDelete({
                  id: original?.node?.id,
                  enabled: enabled,
                  name: original?.node?.displayName,
                })
              }
            >
              <MdArchive />
            </TableButton>
          </>
        );
      },
    },
  ];

  // Server-side paged fetch for <DataTable>. The `where`/`refreshKey` deps make
  // the callback identity change when the filter or a mutation refresh occurs,
  // which re-triggers the table's fetch.
  const fetchData = useCallback(
    ({ pageSize, cursor, filters }) => {
      // Base where (the Show/Hide Disabled toggle) + the Name column filter
      // mapped to the displayName field (AC-15498).
      const where = { createWorkGroup: { eq: !showArchived } };
      const nameFilter = filters?.find((f) => f.id === "name");
      if (nameFilter?.value) {
        const clause = textFilterToWhereClause(nameFilter.value);
        if (clause) where.displayName = clause;
      }

      doFetch({
        query: groups,
        variables: {
          first: pageSize,
          after: cursor,
          where,
          order: {
            displayName: "ASC",
          },
        },
      });
    },
    [doFetch, showArchived, refreshKey]
  );

  const toggleEnabled = ({ id, enabled }) => {
    remove({
      query: updateCreateWorkGroupFlag,
      variables: { groupId: id, createWorkGroupState: !enabled },
    });
  };

  //handle remove rule update
  useEffect(() => {
    if (removalData) {
      setRefreshKey((k) => k + 1);
      setShowConfirm(false);
      setOrgToDelete(null);
      setShowArchived(false);
    }
  }, [removalData]);

  useEffect(() => {
    if (orgToDelete) {
      setShowConfirm(true);
    } else {
      setShowConfirm(false);
    }
  }, [orgToDelete]);

  useEffect(() => {
    if (syncData) {
      setRefreshKey((k) => k + 1);
      setShowConfirm(false);
      setShowArchived(false);
    }
  }, [syncData]);

  return (
    <>
      {showAddGroup ? (
        <Modal
          title={`Add Group to Platform`}
          hide={() => setShowAddGroup(null)}
        >
          <AddGroups setShowAddGroup={setShowAddGroup} />
        </Modal>
      ) : null}

      {showConfirm ? (
        <Modal
          title={`Confirm ${
            orgToDelete?.enabled ? "Disable" : "Activation"
          } of ${orgToDelete?.name}`}
          hide={() => setOrgToDelete(null)}
        >
          <p>
            Are you sure you wish to{" "}
            {orgToDelete?.enabled ? "disable" : "activate"} {orgToDelete?.name}?
          </p>
          {orgToDelete?.enabled ? (
            <p>
              This action will remove all Permissions for the selected group.
            </p>
          ) : null}

          <div>
            <Button
              type="button"
              list="true"
              disabled={removalLoading}
              danger
              onClick={() => toggleEnabled(orgToDelete)}
            >
              {removalLoading ? <Spinner /> : "Yes"}
            </Button>
            <Button
              type="button"
              disabled={removalLoading}
              onClick={() => {
                setOrgToDelete(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      ) : null}

      {!showAddGroup && (
        <Button
          onClick={() => setShowAddGroup((prev) => !prev)}
          list={"true"}
          type="button"
        >
          Find Group
        </Button>
      )}

      <Button
        type="button"
        onClick={() => setShowArchived((prevState) => !prevState)}
      >
        {showArchived ? "Hide " : "Show "} Disabled
      </Button>

      {syncErrors ? <ErrorMessages errors={syncErrors} /> : null}
      {removalErrors ? <ErrorMessages errors={removalErrors} /> : null}
      {errors ? <ErrorMessages errors={errors} /> : null}
      <h3>Groups</h3>
      <DataTable
        key={showArchived ? "archived" : "active"}
        fetchData={fetchData}
        totalCount={totalCount}
        pageInfo={pageInfo}
        loading={loading}
        data={workgroups}
        columns={columnsData}
        columnMenu
        defaultPageSize={50}
        // Server query is hardcoded to order by displayName ASC; built-in header
        // sort would be misleading (it can't reorder across pages). Server-side
        // sort wiring is deferred to the sort/filter UX work in AC-15377.
        disableSortBy
      />
    </>
  );
};

export default Organization;
